import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Plane, Plus, Edit2, Trash2, Search, User, Wrench,
  MapPin, Loader, CheckCircle, AlertCircle, X, UserPlus
} from 'lucide-react';
import AircraftFormModal from './modal/AircraftFormModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertCircle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertCircle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ─── Helper pour formater les erreurs ─────────────────────────────────────────
const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  console.error('Full error:', error);
  
  if (error.response?.data) {
    const data = error.response.data;
    
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join('\n• ');
    }
    
    if (typeof data === 'object') {
      const errors = [];
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          errors.push(`• ${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          errors.push(`• ${key}: ${value}`);
        } else if (value?.msg) {
          errors.push(`• ${key}: ${value.msg}`);
        }
      });
      if (errors.length > 0) return errors.join('\n');
    }
  }
  
  if (error.message) return error.message;
  return defaultMessage;
};

// Conversions USA <-> métrique
const lbsToKg = lbs => lbs ? Math.round(lbs * 0.453592) : '';
const kgToLbs = kg  => kg  ? Math.round(kg  / 0.453592) : '';
const galToL  = gal => gal ? Math.round(gal * 3.78541)  : '';
const lToGal  = l   => l   ? +(l / 3.78541).toFixed(1)  : '';

// Convertit MM/DD/YYYY (FAA) → YYYY-MM-DD (HTML date input)
const faaDateToISO = (d) => {
  if (!d) return '';
  const m = d.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : d;
};

const EMPTY_FORM = {
  owner_id:               '',
  tail_number:            '',
  aircraft_type:          '',
  manufacturer:           '',
  model:                  '',
  year:                   '',
  year_manufactured:      '',
  serial_number:          '',
  callsign:               '',
  icao_code:              '',
  engine_type:            '',
  engine_manufacturer:    '',
  engine_model:           '',
  engine_count:           '',
  seats:                  '',
  fuel_type:              'JetA',
  fuel_capacity_gallons:  '',
  max_fuel_gallons:       '',
  empty_weight_lbs:       '',
  max_takeoff_weight_lbs: '',
  max_landing_weight_lbs: '',
  home_base:              '',
  parking_spot:           '',
  owner_name:             '',
  owner_address:          '',
  owner_city:             '',
  owner_state:            '',
  owner_zip:              '',
  owner_country:          'USA',
  owner_phone:            '',
  owner_email:            '',
  airworthiness_date:     '',
  certificate_issue_date: '',
  cruise_speed_kts:       '140',
  fuel_burn_gph:          '12.0',
};

const EMPTY_NEW_OWNER = {
  email:      '',
  first_name: '',
  last_name:  '',
  phone:      '',
};

const EMPTY_CONTACT = {
  source: 'MANUAL',
  priority: 1,
  contact_name: '',
  company_name: '',
  role: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  phone: '',
  email: '',
  notes: '',
};

const AircraftView = () => {
  const [aircraft, setAircraft]               = useState([]);
  const [clients, setClients]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showModal, setShowModal]             = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [searchTerm, setSearchTerm]           = useState('');
  const [formData, setFormData]               = useState(EMPTY_FORM);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState(null);
  const [lookupStatus, setLookupStatus]       = useState(null);
  const [toast, setToast]                     = useState(null);
  const lookupTimer                           = useRef(null);

  // Contact states
  const [contacts, setContacts]               = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact]   = useState(null);
  const [contactForm, setContactForm]         = useState(EMPTY_CONTACT);

  // ── New Owner sub-modal ────────────────────────────────────
  const [showNewOwnerModal, setShowNewOwnerModal] = useState(false);
  const [newOwnerForm, setNewOwnerForm]           = useState(EMPTY_NEW_OWNER);
  const [savingOwner, setSavingOwner]             = useState(false);
  const [ownerError, setOwnerError]               = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  /* ── Fetch ──────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [acRes, clRes] = await Promise.all([
        axios.get(`${API_BASE}/aircraft/`, { withCredentials: true }),  
        axios.get(`${API_BASE}/owners/`, { withCredentials: true }),    
      ]);
      setAircraft(Array.isArray(acRes.data) ? acRes.data : []);
      setClients(Array.isArray(clRes.data)  ? clRes.data  : []);
    } catch (err) {
      setError('Unable to load data. Please check that the server is running.');
      showToast(formatErrorMessage(err, 'Failed to load aircraft data'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Contact functions
  const fetchContacts = useCallback(async (aircraftId) => {
    if (!aircraftId) return;
    setLoadingContacts(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/aircraft/${aircraftId}/contacts/`,
        { withCredentials: true }
      );
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast('Failed to load contacts', 'error');
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const contactField = (key, value) =>
    setContactForm(prev => ({ ...prev, [key]: value }));

  const openContactForm = (contact = null) => {
    setEditingContact(contact);
    setContactForm(contact
      ? { ...EMPTY_CONTACT, ...contact }
      : { ...EMPTY_CONTACT, priority: contacts.length + 1 }
    );
    setShowContactForm(true);
  };

  const closeContactForm = () => {
    setShowContactForm(false);
    setEditingContact(null);
    setContactForm(EMPTY_CONTACT);
  };

  const handleSaveContact = async () => {
    if (!editingAircraft) return;
    const payload = { ...contactForm };
    payload.priority = parseInt(payload.priority) || 1;
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    try {
      if (editingContact) {
        await axios.put(
          `${API_BASE}/aircraft/${editingAircraft.id}/contacts/${editingContact.id}`,
          payload, { withCredentials: true }
        );
        showToast('Contact updated', 'success');
      } else {
        await axios.post(
          `${API_BASE}/aircraft/${editingAircraft.id}/contacts/`,
          payload, { withCredentials: true }
        );
        showToast('Contact added', 'success');
      }
      await fetchContacts(editingAircraft.id);
      closeContactForm();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error saving contact'), 'error');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!editingAircraft) return;
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(
        `${API_BASE}/aircraft/${editingAircraft.id}/contacts/${contactId}`,
        { withCredentials: true }
      );
      showToast('Contact deleted', 'success');
      await fetchContacts(editingAircraft.id);
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error deleting contact'), 'error');
    }
  };

  /* ── Auto-lookup ────────────────────────────────────────── */
  const handleTailNumberChange = (value) => {
    const tail = value.toUpperCase();
    field('tail_number', tail);
    setLookupStatus(null);
    if (tail.length < 4) return;
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => triggerLookup(tail), 800);
  };

  const triggerLookup = async (tail) => {
    setLookupStatus('loading');
    try {
      const { data } = await axios.get(`${API_BASE}/lookup/${tail}`, { withCredentials: true });
      console.log('[Lookup]', data);
      setFormData(prev => ({
        ...prev,
        manufacturer:           data.manufacturer           || prev.manufacturer,
        model:                  data.model                  || prev.model,
        aircraft_type:          data.aircraft_type          || prev.aircraft_type,
        year_manufactured:      data.year_manufactured      || prev.year_manufactured,
        serial_number:          data.serial_number          || prev.serial_number,
        icao_code:              data.icao_code              || prev.icao_code,
        engine_type:            data.engine_type            || prev.engine_type,
        engine_manufacturer:    data.engine_manufacturer    || prev.engine_manufacturer,
        engine_model:           data.engine_model           || prev.engine_model,
        engine_count:           data.engine_count           || prev.engine_count,
        airworthiness_date:     faaDateToISO(data.airworthiness_date)     || prev.airworthiness_date,
        certificate_issue_date: faaDateToISO(data.certificate_issue_date) || prev.certificate_issue_date,
        max_takeoff_weight_lbs: data.max_takeoff_weight_lbs || prev.max_takeoff_weight_lbs,
        max_landing_weight_lbs: data.max_landing_weight_lbs || prev.max_landing_weight_lbs,
        owner_name:    data.owner_name    || prev.owner_name,
        owner_address: data.owner_address || prev.owner_address,
        owner_city:    data.owner_city    || prev.owner_city,
        owner_state:   data.owner_state   || prev.owner_state,
        owner_zip:     data.owner_zip     || prev.owner_zip,
        owner_country: data.owner_country || prev.owner_country,
        owner_phone:   data.owner_phone   || prev.owner_phone,
        cruise_speed_kts: data.cruise_speed_kts || prev.cruise_speed_kts,
        fuel_burn_gph:    data.fuel_burn_gph    || prev.fuel_burn_gph,
      }));
      setLookupStatus('found');
      showToast('FAA data imported successfully', 'success');
    } catch (err) {
      setLookupStatus(err.response?.status === 404 ? 'notfound' : 'error');
      if (err.response?.status !== 404) {
        showToast(formatErrorMessage(err, 'FAA lookup failed'), 'error');
      }
    }
  };

  /* ── Submit aircraft ────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!formData.tail_number) {
      showToast('Tail number is required', 'warning');
      return;
    }
    setSaving(true);
    try {
      const toInt = (value) => {
        if (value === undefined || value === null || value === '') return undefined;
        const num = parseInt(value);
        return isNaN(num) ? undefined : num;
      };
      
      const toFloat = (value) => {
        if (value === undefined || value === null || value === '') return undefined;
        const num = parseFloat(value);
        return isNaN(num) ? undefined : num;
      };

      const lbs_mtow = formData.max_takeoff_weight_lbs ? toInt(formData.max_takeoff_weight_lbs) : undefined;
      const lbs_mlw  = formData.max_landing_weight_lbs  ? toInt(formData.max_landing_weight_lbs)  : undefined;
      const gal_cap  = formData.fuel_capacity_gallons  ? toFloat(formData.fuel_capacity_gallons) : undefined;
      const gal_max  = formData.max_fuel_gallons        ? toFloat(formData.max_fuel_gallons)      : undefined;

      const payload = {
        tail_number:            formData.tail_number?.toUpperCase(),
        aircraft_type:          formData.aircraft_type || undefined,
        manufacturer:           formData.manufacturer || undefined,
        model:                  formData.model || undefined,
        serial_number:          formData.serial_number || undefined,
        callsign:               formData.callsign?.toUpperCase() || undefined,
        icao_code:              formData.icao_code?.toUpperCase() || undefined,
        engine_type:            formData.engine_type || undefined,
        engine_manufacturer:    formData.engine_manufacturer || undefined,
        engine_model:           formData.engine_model || undefined,
        fuel_type:              formData.fuel_type || undefined,
        home_base:              formData.home_base?.toUpperCase() || undefined,
        parking_spot:           formData.parking_spot || undefined,
        owner_name:             formData.owner_name || undefined,
        owner_address:          formData.owner_address || undefined,
        owner_city:             formData.owner_city || undefined,
        owner_state:            formData.owner_state?.toUpperCase() || undefined,
        owner_zip:              formData.owner_zip || undefined,
        owner_country:          formData.owner_country || undefined,
        owner_phone:            formData.owner_phone || undefined,
        owner_email:            formData.owner_email || undefined,
        owner_id:               formData.owner_id || undefined,
        year:                   toInt(formData.year),
        year_manufactured:      toInt(formData.year_manufactured),
        seats:                  toInt(formData.seats),
        engine_count:           toInt(formData.engine_count),
        cruise_speed_kts:       toInt(formData.cruise_speed_kts) || 140,
        fuel_burn_gph:          toFloat(formData.fuel_burn_gph) || 12.0,
        max_takeoff_weight_lbs: lbs_mtow,
        max_takeoff_weight_kg:  lbs_mtow ? Math.round(lbs_mtow * 0.453592) : undefined,
        max_landing_weight_lbs: lbs_mlw,
        max_landing_weight_kg:  lbs_mlw ? Math.round(lbs_mlw * 0.453592) : undefined,
        mtow_kg:                lbs_mtow ? Math.round(lbs_mtow * 0.453592) : undefined,
        empty_weight_lbs:       toInt(formData.empty_weight_lbs),
        empty_weight_kg:        toInt(formData.empty_weight_lbs) ? Math.round(toInt(formData.empty_weight_lbs) * 0.453592) : undefined,
        fuel_capacity_gallons:  gal_cap,
        fuel_capacity_liters:   gal_cap ? Math.round(gal_cap * 3.78541) : undefined,
        max_fuel_gallons:       gal_max,
        max_fuel_liters:        gal_max ? Math.round(gal_max * 3.78541) : undefined,
        airworthiness_date:     formData.airworthiness_date || undefined,
        certificate_issue_date: formData.certificate_issue_date || undefined,
      };

      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log('📤 PAYLOAD CORRIGÉ:', JSON.stringify(payload, null, 2));

      if (editingAircraft) {
        await axios.put(`${API_BASE}/aircraft/${editingAircraft.id}`, payload, { withCredentials: true });
        showToast(`Aircraft ${formData.tail_number} updated successfully`, 'success');
      } else {
        await axios.post(`${API_BASE}/aircraft`, payload, { withCredentials: true });
        showToast(`Aircraft ${formData.tail_number} created successfully`, 'success');
      }
      await fetchData();
      closeModal();
    } catch (err) {
      console.log('🔴 ERREUR:', err.response?.data);
      showToast(formatErrorMessage(err, 'Error saving aircraft'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ─────────────────────────────────────────────── */
  const handleDelete = async (ac) => {
    if (!window.confirm(`Delete aircraft ${ac.tail_number}?`)) return;
    try {
      await axios.delete(`${API_BASE}/aircraft/${ac.id}`, { withCredentials: true });
      showToast(`Aircraft ${ac.tail_number} deleted successfully`, 'success');
      await fetchData();
    } catch (err) {
      showToast(formatErrorMessage(err, 'Error deleting aircraft'), 'error');
    }
  };

  /* ── Modal helpers ──────────────────────────────────────── */
  const openCreateModal = () => {
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    setContacts([]);
    setShowContactForm(false);
    setShowModal(true);
  };

  const openEditModal = (ac) => {
    setEditingAircraft(ac);
    setFormData({
      owner_id:               ac.owner_id               ?? '',
      tail_number:            ac.tail_number             ?? '',
      aircraft_type:          ac.aircraft_type           ?? '',
      manufacturer:           ac.manufacturer            ?? '',
      model:                  ac.model                   ?? '',
      year:                   ac.year                    ?? '',
      year_manufactured:      ac.year_manufactured       ?? '',
      serial_number:          ac.serial_number           ?? '',
      callsign:               ac.callsign                ?? '',
      icao_code:              ac.icao_code               ?? '',
      engine_type:            ac.engine_type             ?? '',
      engine_manufacturer:    ac.engine_manufacturer     ?? '',
      engine_model:           ac.engine_model            ?? '',
      engine_count:           ac.engine_count            ?? '',
      seats:                  ac.seats                   ?? '',
      fuel_type:              ac.fuel_type               ?? 'JetA',
      fuel_capacity_gallons:  ac.fuel_capacity_gallons  ?? (ac.fuel_capacity_liters ? lToGal(ac.fuel_capacity_liters) : ''),
      max_fuel_gallons:       ac.max_fuel_gallons       ?? (ac.max_fuel_liters      ? lToGal(ac.max_fuel_liters)      : ''),
      empty_weight_lbs:       ac.empty_weight_lbs       ?? '',
      max_takeoff_weight_lbs: ac.max_takeoff_weight_lbs ?? (ac.max_takeoff_weight_kg ? kgToLbs(ac.max_takeoff_weight_kg) : '') ?? (ac.mtow_kg ? kgToLbs(ac.mtow_kg) : ''),
      max_landing_weight_lbs: ac.max_landing_weight_lbs ?? (ac.max_landing_weight_kg ? kgToLbs(ac.max_landing_weight_kg) : ''),
      home_base:              ac.home_base              ?? '',
      parking_spot:           ac.parking_spot           ?? '',
      owner_name:             ac.owner_name             ?? '',
      owner_address:          ac.owner_address          ?? '',
      owner_city:             ac.owner_city             ?? '',
      owner_state:            ac.owner_state            ?? '',
      owner_zip:              ac.owner_zip              ?? '',
      owner_country:          ac.owner_country          ?? 'USA',
      owner_phone:            ac.owner_phone            ?? '',
      owner_email:            ac.owner_email            ?? '',
      airworthiness_date:     ac.airworthiness_date     ?? '',
      certificate_issue_date: ac.certificate_issue_date ?? '',
      cruise_speed_kts:       ac.cruise_speed_kts       ?? '140',
      fuel_burn_gph:          ac.fuel_burn_gph          ?? '12.0',
    });
    setLookupStatus(null);
    setShowModal(true);
    fetchContacts(ac.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAircraft(null);
    setFormData(EMPTY_FORM);
    setLookupStatus(null);
    setContacts([]);
    setShowContactForm(false);
    clearTimeout(lookupTimer.current);
  };

  const field = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  /* ── New Owner sub-modal ────────────────────────────────── */
  const openNewOwnerModal = () => {
    setNewOwnerForm({
      email:      formData.owner_email || '',
      first_name: '',
      last_name:  formData.owner_name  || '',
      phone:      formData.owner_phone || '',
    });
    setOwnerError(null);
    setShowNewOwnerModal(true);
  };

  const closeNewOwnerModal = () => {
    setShowNewOwnerModal(false);
    setNewOwnerForm(EMPTY_NEW_OWNER);
    setOwnerError(null);
  };

  const ownerField = (key, value) =>
    setNewOwnerForm(prev => ({ ...prev, [key]: value }));

  const handleCreateOwner = async () => {
    if (!newOwnerForm.email) {
      setOwnerError('Email is required.');
      return;
    }
    setSavingOwner(true);
    setOwnerError(null);
    try {
      const { data: newOwner } = await axios.post(`${API_BASE}/owners/`, newOwnerForm, { withCredentials: true });
      setClients(prev => [...prev, newOwner]);
      field('owner_id', newOwner.id);
      showToast(`Owner ${newOwner.email} created successfully`, 'success');
      closeNewOwnerModal();
    } catch (err) {
      setOwnerError(formatErrorMessage(err, 'Could not create owner.'));
    } finally {
      setSavingOwner(false);
    }
  };

  /* ── Filter ─────────────────────────────────────────────── */
  const filtered = aircraft.filter(a =>
    a.tail_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.aircraft_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.owner_company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* ── Loading / Error ────────────────────────────────────── */
  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />Loading…
    </div>
  );
  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 px-4">
      <p className="text-red-400 text-center">{error}</p>
      <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Retry</button>
    </div>
  );

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Aircraft Management</h2>
          <p className="text-gray-400 text-xs md:text-sm mt-1">Aircraft registered · {aircraft.length} total</p>
        </div>
        <button onClick={openCreateModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition text-sm sm:text-base w-full sm:w-auto justify-center">
          <Plus size={18} /> New aircraft
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input type="text" placeholder="Search by tail number, type or owner…"
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm md:text-base" />
      </div>

      {/* Cards */}
      {filtered.length === 0
        ? <p className="text-center text-gray-500 py-16">No aircraft found.</p>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filtered.map(ac => (
              <div key={ac.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-3 md:p-4 hover:border-gray-600 transition">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Plane className="text-blue-400 flex-shrink-0" size={20} />
                    <div className="min-w-0">
                      <h3 className="text-white font-bold text-sm md:text-base truncate">{ac.tail_number}</h3>
                      <p className="text-gray-400 text-xs truncate">
                        {ac.aircraft_type || `${ac.manufacturer || ''} ${ac.model || ''}`.trim() || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEditModal(ac)} className="text-blue-400 hover:text-blue-300 p-1"><Edit2 size={16} /></button>
                    <button onClick={() => handleDelete(ac)}  className="text-red-400  hover:text-red-300  p-1"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="space-y-1 text-xs md:text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <User size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{ac.owner_name || ac.owner_company || 'Unknown owner'}</span>
                  </div>
                  {(ac.owner_city || ac.owner_state) && (
                    <div className="flex items-center gap-2 text-gray-400 text-xs pl-5">
                      <span>{[ac.owner_city, ac.owner_state, ac.owner_zip].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-300">
                    <Wrench size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">{[ac.manufacturer, ac.model].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <MapPin size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="truncate">
                      {ac.fuel_type || '—'}
                      {ac.fuel_capacity_gallons
                        ? ` · ${ac.fuel_capacity_gallons} gal`
                        : ac.fuel_capacity_liters ? ` · ${ac.fuel_capacity_liters} L` : ''}
                      {ac.parking_spot ? ` · ${ac.parking_spot}` : ''}
                    </span>
                  </div>
                  {(ac.max_takeoff_weight_lbs || ac.mtow_kg) && (
                    <div className="text-xs text-gray-500">
                      MTOW: {ac.max_takeoff_weight_lbs
                        ? `${ac.max_takeoff_weight_lbs.toLocaleString()} lbs`
                        : `${ac.mtow_kg?.toLocaleString()} kg`}
                    </div>
                  )}
                  {(ac.cruise_speed_kts || ac.fuel_burn_gph) && (
                    <div className="text-xs text-gray-500 flex gap-3 mt-1">
                      {ac.cruise_speed_kts && (
                        <span>✈ {ac.cruise_speed_kts} kts</span>
                      )}
                      {ac.fuel_burn_gph && (
                        <span>⛽ {ac.fuel_burn_gph} GPH</span>
                      )}
                    </div>
                  )}
                  {ac.year_manufactured && <div className="text-xs text-gray-500">Year: {ac.year_manufactured}</div>}
                </div>
              </div>
            ))}
          </div>
        )
      }

      {/* Aircraft Form Modal */}
      <AircraftFormModal
        isOpen={showModal}
        editingAircraft={editingAircraft}
        formData={formData}
        clients={clients}
        lookupStatus={lookupStatus}
        saving={saving}
        contacts={contacts}
        loadingContacts={loadingContacts}
        showContactForm={showContactForm}
        editingContact={editingContact}
        contactForm={contactForm}
        onClose={closeModal}
        onFieldChange={field}
        onTailNumberChange={handleTailNumberChange}
        onSubmit={handleSubmit}
        onOpenNewOwnerModal={openNewOwnerModal}
        onFetchContacts={fetchContacts}
        onContactField={contactField}
        onOpenContactForm={openContactForm}
        onCloseContactForm={closeContactForm}
        onSaveContact={handleSaveContact}
        onDeleteContact={handleDeleteContact}
      />

      {/* ── New Owner sub-modal ── */}
      {showNewOwnerModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <UserPlus size={18} className="text-green-400" />
                <h3 className="text-white font-bold">New Owner</h3>
              </div>
              <button onClick={closeNewOwnerModal} className="text-gray-400 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {ownerError && (
                <div className="bg-red-900/40 border border-red-700 text-red-300 text-xs rounded-lg px-3 py-2">
                  {ownerError}
                </div>
              )}
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Email *</label>
                <input
                  type="email"
                  placeholder="pilot@example.com"
                  value={newOwnerForm.email}
                  onChange={e => ownerField('email', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">First Name</label>
                  <input
                    type="text"
                    value={newOwnerForm.first_name}
                    onChange={e => ownerField('first_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-xs mb-1 block">Last Name</label>
                  <input
                    type="text"
                    value={newOwnerForm.last_name}
                    onChange={e => ownerField('last_name', e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Phone</label>
                <input
                  type="tel"
                  placeholder="830-336-2518"
                  value={newOwnerForm.phone}
                  onChange={e => ownerField('phone', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-700 flex justify-end gap-2">
              <button onClick={closeNewOwnerModal}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition">
                Cancel
              </button>
              <button onClick={handleCreateOwner} disabled={savingOwner || !newOwnerForm.email}
                className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition flex items-center gap-2">
                {savingOwner
                  ? <><Loader size={14} className="animate-spin" /> Creating…</>
                  : <><UserPlus size={14} /> Create & Select</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

    </div>
  );
};

export default AircraftView;